import React, { useState, useEffect, useCallback } from 'react'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useAuthStore } from '../../store/useAuthStore'
import { apiRequest } from '../../utils/api'
import { buildMonthlyReportPages } from '../../utils/reportPdfBuilder'
import MantineSelectBox from '../common/MantineSelectBox'
import SaveButton from '../common/SaveButton'

const MON = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const fmtDate = (dt) => {
  if (!dt) return null
  const d = new Date(dt)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
}

const defaultMonth = (() => {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
})()

const ReportsBox = () => {
  const user              = useAuthStore((s) => s.user)
  const isViewingAsManager = useAuthStore((s) => s.isViewingAsManager)
  const currentCenter     = useAuthStore((s) => s.currentCenter)

  // 순수 어드민(다른 센터 조회 가능) vs 센터 고정 상태
  const isPureAdmin   = user?.STATUS === 'A' && !isViewingAsManager
  const lockedCenterID = isPureAdmin ? null : String(user?.CENTER_ID || currentCenter?.CENTER_ID || '')
  const lockedCenterName = isPureAdmin ? null : (currentCenter?.NAME || '')

  const [selectedMonth, setSelectedMonth]   = useState(defaultMonth)
  const [centerFilter, setCenterFilter]     = useState('')
  const [centerOptions, setCenterOptions]   = useState([])
  const [students, setStudents]             = useState([])
  const [isLoading, setIsLoading]           = useState(false)
  const [isGeneratingAll, setIsGeneratingAll] = useState(false)
  const [downloadingId, setDownloadingId]   = useState(null)
  const [regenId, setRegenId]               = useState(null)

  // 센터 목록 로드 (순수 어드민만)
  useEffect(() => {
    if (!isPureAdmin) return
    apiRequest('/centers/list', 'POST').then((res) => {
      if (res?.data) {
        setCenterOptions([
          { value: '', label: 'All Centers' },
          ...res.data.map((c) => ({ value: String(c.CENTER_ID), label: c.NAME })),
        ])
      }
    })
  }, [isPureAdmin])

  const fetchOverview = useCallback(async () => {
    const [yearStr, monthStr] = selectedMonth.split('-')
    setIsLoading(true)
    try {
      const params = new URLSearchParams({ year: yearStr, month: monthStr })
      const effectiveCenter = isPureAdmin ? centerFilter : lockedCenterID
      if (effectiveCenter) params.set('CENTER_ID', effectiveCenter)
      const res = await apiRequest(`/admin/reports/overview?${params}`, 'GET')
      if (res?.success) setStudents(res.students || [])
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to load student list', color: 'red' })
    } finally {
      setIsLoading(false)
    }
  }, [selectedMonth, centerFilter, isPureAdmin, lockedCenterID])

  useEffect(() => { fetchOverview() }, [fetchOverview])

  const [sortKey, setSortKey]   = useState('name')
  const [sortDir, setSortDir]   = useState('asc')

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('asc') }
  }

  const [yearStr, monthStr] = selectedMonth.split('-')
  const year  = Number(yearStr)
  const month = Number(monthStr)

  const savedCount = students.filter((s) => s.report_saved_at).length

  const isGenerationAllowed = year > 2026 || (year === 2026 && month >= 5)

  const sortedStudents = [...students].sort((a, b) => {
    let va, vb
    if (sortKey === 'name')   { va = a.NAME; vb = b.NAME }
    else if (sortKey === 'center') { va = a.CENTER_NAME; vb = b.CENTER_NAME }
    else if (sortKey === 'status') { va = a.report_saved_at ? 1 : 0; vb = b.report_saved_at ? 1 : 0 }
    else if (sortKey === 'date')   { va = a.report_saved_at || ''; vb = b.report_saved_at || '' }
    else { va = ''; vb = '' }
    if (va < vb) return sortDir === 'asc' ? -1 : 1
    if (va > vb) return sortDir === 'asc' ? 1 : -1
    return 0
  })

  // PDF 다운로드
  const handleDownload = async (student) => {
    setDownloadingId(student.USER_ID)
    try {
      const res = await apiRequest('/report/monthly', 'POST', {
        USER_ID:    student.USER_ID,
        NUMERIC_ID: student.NUMERIC_ID,
        CENTER_ID:  student.CENTER_ID,
        year,
        month,
      })
      if (!res?.success) {
        notifications.show({ title: 'No Data', message: 'No report data available.', color: 'yellow' })
        return
      }
      const pages    = buildMonthlyReportPages(res.data)
      const pdf      = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfWidth = pdf.internal.pageSize.getWidth()
      for (let i = 0; i < pages.length; i++) {
        const container = document.createElement('div')
        container.style.cssText = 'position:fixed;left:-9999px;top:0;width:870px;background:#F8FAFC;'
        container.innerHTML = pages[i]
        document.body.appendChild(container)
        await new Promise((r) => setTimeout(r, 350))
        const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#F8FAFC' })
        document.body.removeChild(container)
        const imgH = (canvas.height * pdfWidth) / canvas.width
        if (i > 0) pdf.addPage()
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pdfWidth, imgH)
      }
      pdf.save(`report_${student.NAME}_${selectedMonth}.pdf`)
      notifications.show({ title: 'Downloaded', message: `${student.NAME} report downloaded`, color: 'green' })
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to generate PDF', color: 'red' })
    } finally {
      setDownloadingId(null)
    }
  }

  // 단일 재생성
  const handleRegen = async (student) => {
    setRegenId(student.USER_ID)
    try {
      const res = await apiRequest('/admin/report/generate-one', 'POST', {
        USER_ID:    student.USER_ID,
        NUMERIC_ID: student.NUMERIC_ID,
        year,
        month,
      })
      if (res?.success) {
        notifications.show({ title: 'Done', message: `${student.NAME} report regenerated`, color: 'green' })
        fetchOverview()
      } else {
        notifications.show({ title: 'No Data', message: res?.message || 'No data available', color: 'yellow' })
      }
    } catch (e) {
      notifications.show({ title: 'Error', message: 'Failed to regenerate report', color: 'red' })
    } finally {
      setRegenId(null)
    }
  }

  // 전체 생성
  const handleGenerateAll = () => {
    modals.openConfirmModal({
      title: 'Generate All Reports',
      children: (
        <div style={{ fontSize: 14, lineHeight: 1.6 }}>
          Generate reports for <b>{MON[month]} {yearStr}</b>.
          {!isPureAdmin
            ? ` (${lockedCenterName || 'my center'} only)`
            : centerFilter && centerOptions.find((c) => c.value === centerFilter)
              ? ` (${centerOptions.find((c) => c.value === centerFilter).label} only)`
              : ' (all students)'}
          <br />This includes GPT calls and may take several minutes. Proceed?
        </div>
      ),
      labels: { confirm: 'Generate', cancel: 'Cancel' },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        setIsGeneratingAll(true)
        notifications.show({
          id: 'gen-all', title: 'Generating...', loading: true, autoClose: false,
          message: `Generating reports for ${MON[month]} ${yearStr}...`, color: 'blue',
        })
        try {
          const effectiveCenter = isPureAdmin ? (centerFilter || undefined) : (lockedCenterID || undefined)
          const res = await apiRequest('/admin/report/generate-all', 'POST', {
            year, month, CENTER_ID: effectiveCenter ? Number(effectiveCenter) : undefined,
          })
          notifications.hide('gen-all')
          if (res?.success !== undefined) {
            notifications.show({
              title: 'Generation Complete',
              message: `Success: ${res.success} · Skipped: ${res.skipped} · Failed: ${res.failed}`,
              color: 'green', autoClose: 8000,
            })
            fetchOverview()
          }
        } catch (e) {
          notifications.hide('gen-all')
          notifications.show({ title: 'Error', message: 'An error occurred during generation', color: 'red' })
        } finally {
          setIsGeneratingAll(false)
        }
      },
    })
  }

  return (
    <div style={{ padding: '28px 32px' }}>

      {/* ── 헤더 ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#1a2f4c', letterSpacing: '-0.02em' }}>
          Reports
        </div>
        <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>
          View and generate monthly reports per student
        </div>
      </div>

      {/* ── 컨트롤 바 ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>

        {/* 월 선택 */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '0 12px',
          height: 36, border: '1px solid #D0D5DD', borderRadius: 8,
          background: '#fff', fontSize: 13, color: '#344054',
        }}>
          <span style={{ color: '#98A2B3' }}>📅</span>
          <select
            value={yearStr}
            onChange={(e) => {
              const ny = e.target.value
              const curY = new Date().getFullYear()
              const curM = new Date().getMonth() + 1
              const sm   = parseInt(monthStr, 10)
              const maxM = parseInt(ny, 10) === curY ? curM - 1 : 12
              setSelectedMonth(`${ny}-${String(Math.min(sm, maxM < 1 ? 1 : maxM)).padStart(2,'0')}`)
            }}
            style={{ border: 'none', outline: 'none', fontSize: 13, background: 'transparent', cursor: 'pointer' }}
          >
            {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <span style={{ color: '#D0D5DD', margin: '0 2px' }}>/</span>
          <select
            value={monthStr}
            onChange={(e) => setSelectedMonth(`${yearStr}-${e.target.value}`)}
            style={{ border: 'none', outline: 'none', fontSize: 13, background: 'transparent', cursor: 'pointer' }}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => {
              const curY = new Date().getFullYear()
              const curM = new Date().getMonth() + 1
              return parseInt(yearStr, 10) < curY || m < curM
            }).map((m) => (
              <option key={m} value={String(m).padStart(2,'0')}>{MON[m]}</option>
            ))}
          </select>
        </div>

        {/* 센터 필터: 순수 어드민만 드롭다운 / 매니저·ViewingAsManager는 센터명 칩 */}
        {isPureAdmin && centerOptions.length > 0 ? (
          <MantineSelectBox
            value={centerFilter}
            data={centerOptions}
            onChange={setCenterFilter}
            width="180px"
          />
        ) : lockedCenterName ? (
          <div style={{
            padding: '0 12px', height: 36, display: 'flex', alignItems: 'center',
            borderRadius: 8, background: '#EFF6FF', border: '1px solid #BFDBFE',
            fontSize: 13, fontWeight: 600, color: '#1D4ED8',
          }}>
            🏫 {lockedCenterName}
          </div>
        ) : null}

        <SaveButton onClick={fetchOverview} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Refresh'}
        </SaveButton>

        <SaveButton
          onClick={handleGenerateAll}
          disabled={isGeneratingAll || isLoading || !isGenerationAllowed}
          style={{
            background: isGenerationAllowed ? '#1a2f4c' : '#E5E7EB',
            color: isGenerationAllowed ? '#fff' : '#9CA3AF',
            borderColor: isGenerationAllowed ? '#1a2f4c' : '#E5E7EB',
            cursor: isGenerationAllowed ? 'pointer' : 'not-allowed',
          }}
          title={!isGenerationAllowed ? 'Generation disabled for months before May 2026' : ''}
        >
          {isGeneratingAll ? 'Generating...' : `Generate All (${MON[month]} ${year})`}
        </SaveButton>

        {/* 요약 칩 */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: '#F0FDF4', color: '#16a34a', border: '1px solid #BBF7D0',
          }}>
            Saved {savedCount}
          </span>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: '#FFF7ED', color: '#c2410c', border: '1px solid #FED7AA',
          }}>
            Not saved {students.length - savedCount}
          </span>
        </div>
      </div>

      {/* ── 테이블 ── */}
      <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E7EB', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              {[
                { label: 'No',           key: null },
                { label: 'Name',         key: 'name' },
                { label: 'Center',       key: 'center' },
                { label: 'Status',       key: 'status' },
                { label: 'Generated At', key: 'date' },
                { label: 'Actions',      key: null },
              ].map(({ label, key }) => (
                <th
                  key={label}
                  onClick={key ? () => handleSort(key) : undefined}
                  style={{
                    padding: '11px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 700, color: key ? '#374151' : '#6B7280',
                    letterSpacing: '0.06em',
                    cursor: key ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                  {key && (
                    <span style={{ marginLeft: 4, color: sortKey === key ? '#1a2f4c' : '#D1D5DB' }}>
                      {sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>Loading...</td></tr>
            ) : sortedStudents.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 14 }}>No students found.</td></tr>
            ) : (
              sortedStudents.map((s, idx) => {
                const isSaved    = !!s.report_saved_at
                const isDlLoading = downloadingId === s.USER_ID
                const isRgLoading = regenId === s.USER_ID

                return (
                  <tr
                    key={s.USER_ID}
                    style={{
                      borderBottom: '1px solid #F3F4F6',
                      background: isSaved ? '#fff' : '#FAFAFA',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F9FF' }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isSaved ? '#fff' : '#FAFAFA' }}
                  >
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#9CA3AF', width: 48 }}>{idx + 1}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: '#111827' }}>
                      {s.NAME}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#6B7280' }}>
                      {s.CENTER_NAME}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {isSaved ? (
                        <span style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: '#F0FDF4', color: '#16a34a', border: '1px solid #BBF7D0',
                        }}>Saved</span>
                      ) : (
                        <span style={{
                          padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                          background: '#F9FAFB', color: '#9CA3AF', border: '1px solid #E5E7EB',
                        }}>Not saved</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: '#6B7280' }}>
                      {isSaved ? fmtDate(s.report_saved_at) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {isSaved && (
                          <button
                            onClick={() => handleDownload(s)}
                            disabled={isDlLoading}
                            style={{
                              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                              background: isDlLoading ? '#E5E7EB' : '#1a2f4c', color: '#fff',
                              border: 'none', cursor: isDlLoading ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isDlLoading ? '...' : '↓ PDF'}
                          </button>
                        )}
                        {isGenerationAllowed && (!isSaved || isPureAdmin) && (
                          <button
                            onClick={() => handleRegen(s)}
                            disabled={isRgLoading}
                            style={{
                              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                              background: isRgLoading ? '#E5E7EB' : '#fff',
                              color: isRgLoading ? '#9CA3AF' : '#374151',
                              border: '1px solid #D0D5DD', cursor: isRgLoading ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isRgLoading ? '...' : isSaved ? '↺ Regen' : '+ Generate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {students.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
          {students.length} students · {MON[month]} {year}
        </div>
      )}
    </div>
  )
}

export default ReportsBox
